// HeadlessDxfReport.jsx
//
// GET-navigable, automated DXF generator:
//   /generate-dxf/:token/:designID  (optionally ?returnUrl=...)
//
// Fully standalone: DXF is produced purely from currentDesign.model_data, so
// this route does NOT mount the 3D Designer. It verifies the user, fetches the
// design, generates + uploads the DXF, then reports status (window global +
// #report-status JSON + optional redirect). It does not modify any existing
// component or flow.

import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { checkUser, getDesign } from "../../helpers/API";
import { generateDxf } from "../../ThreeJs/Files/Dxf/DxfGenerator";
import { uploadDesignerReport, resolveReportProjectId } from "../../helpers/reportUpload";
import {
  HeadlessReportController,
  REPORT_PHASES,
} from "./HeadlessReportController";
import HeadlessReportScreen from "./HeadlessReportScreen";

const HeadlessDxfReport = () => {
  const { token, designID } = useParams();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";

  const [status, setStatus] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard against double-invocation (e.g. React 18 StrictMode dev remount).
    if (ranRef.current) return;
    ranRef.current = true;

    const controller = new HeadlessReportController({
      reportType: "dxf",
      returnUrl,
      onChange: setStatus,
    });

    const run = async () => {
      if (!token || !designID) {
        controller.fail("missing-params", "Token or design ID missing from URL.");
        return;
      }

      // 1) Auth
      controller.setPhase(REPORT_PHASES.AUTHENTICATING, { progress: 10 });
      try {
        const res = await checkUser(token);
        if (res?.status !== 200) {
          controller.fail("unauthenticated", "User verification failed.");
          return;
        }
      } catch (e) {
        controller.fail("unauthenticated", "User verification failed.");
        return;
      }

      // 2) Fetch design
      controller.setPhase(REPORT_PHASES.LOADING_DESIGN, { progress: 30 });
      let currentDesign;
      try {
        const res = await getDesign(token, designID);
        if (res?.status !== 200 || !res?.data?.data) {
          controller.fail("design-not-found", "Design could not be retrieved.");
          return;
        }
        currentDesign = res.data.data;
      } catch (e) {
        controller.fail("design-not-found", "Design could not be retrieved.");
        return;
      }

      // 3) Parse model data
      controller.setPhase(REPORT_PHASES.GENERATING, { progress: 60 });
      let dxfPolygons;
      try {
        const parsed = JSON.parse(currentDesign.model_data);
        dxfPolygons = Array.isArray(parsed) ? parsed : parsed?.polygons;
      } catch (e) {
        controller.fail("invalid-model-data", "model_data is not valid JSON.");
        return;
      }

      if (!Array.isArray(dxfPolygons) || dxfPolygons.length === 0) {
        controller.fail("no-segments", "No sections found to generate a DXF.");
        return;
      }

      // 4) Generate + upload
      try {
        // shouldDownload:false suppresses the <a download> click in print()
        // that would otherwise pop the browser "Save as" dialog. On this
        // headless route the file is delivered via upload + redirect only.
        const result = generateDxf(dxfPolygons, currentDesign?.name || "design", {
          shouldDownload: false,
        });
        if (!result?.blob) {
          controller.fail("generation-failed", "DXF generation produced no file.");
          return;
        }

        const upload = await uploadDesignerReport({
          token: token || "",
          projectId: resolveReportProjectId(currentDesign, designID),
          reportType: "dxf",
          fileBlob: result.blob,
          fileName: result.fileName,
          // This route reports failure through its own status controller.
          notifyOnError: false,
        });

        if (!upload?.ok) {
          controller.fail(
            "upload-failed",
            `Report upload failed (status ${upload?.status ?? 0}).`
          );
          return;
        }

        controller.succeed("DXF generated and uploaded.");
      } catch (e) {
        console.error("Headless DXF generation failed:", e);
        controller.fail("generation-failed", "DXF generation threw an error.");
      }
    };

    run();
  }, [token, designID, returnUrl]);

  return <HeadlessReportScreen reportType="dxf" status={status} />;
};

export default HeadlessDxfReport;
